import * as api from "../../services/api";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useFeedbackTools} from "../../components/FeedbackTools";
import {eachMonthOfInterval, endOfMonth, formatISO, parseISO, startOfMonth} from "date-fns";


/*
 * Query to get all the rates for an apartment
 */
export const useApartmentRatesQuery = (apartmentId) => {
    return useQuery({
        queryKey: ["rates", apartmentId],
        queryFn: () => api.RatesAPI.getRates(apartmentId),
        enabled: !!apartmentId
    });
}

/*
 * Mutation to delete a rate
 */
export const useDeleteRateMutation = ({apartmentId, rateId}) => {
    const queryClient = useQueryClient();
    const {showSuccessNotification} = useFeedbackTools();

    return useMutation({
        mutationFn: async () => await api.RatesAPI.deletePrices(rateId),
        onSuccess: () => {
            // Invalidate all the query that depends on the deleted rate
            queryClient.invalidateQueries({
                queryKey: ["apartmentPrices", apartmentId]
            });
            showSuccessNotification("Prezzi eliminati con successo");
        }
    });
}

/*
 * Mutation to update a rate
 */
export const useUpdateRateMutation = ({apartmentId, rateId}) => {
    const queryClient = useQueryClient();
    const {showSuccessNotification, showErrorNotification} = useFeedbackTools();

    return useMutation({
        mutationFn: async (data) => await api.RatesAPI.updateRate(rateId, data),
        onSuccess: () => {
            // Invalidate all the query that depends on the updated rate
            queryClient.invalidateQueries({
                queryKey: ["rates", apartmentId]
            });
            // Invalidated the prices query since when the minPeople is decreased or the maxPeople is increased
            // The prices are deleted by the backend
            queryClient.invalidateQueries({
                queryKey: ["apartmentPrices", apartmentId]
            });
            showSuccessNotification("Tariffa aggiornata con successo");
        },
        onError: (error) => {
            showErrorNotification(error)
        }
    });
}

/*
 * Query to get all the prices for one apartment
 */
export const useApartmentPricesQuery = ({apartmentId, dateFromISO, dateToISO}) => {
    return useQuery(
        ["apartmentPrices", apartmentId, dateFromISO, dateToISO],
        () => api.RatesAPI.getPrices(apartmentId, dateFromISO, dateToISO),
        {
            staleTime: 1000 * 60 * 2,  // 2 minutes
            cacheTime: 1000 * 60 * 60 * 2,  // 24 hours
            enabled: !!dateFromISO && !!dateToISO && (apartmentId !== null) && (apartmentId !== undefined),
        }
    );
}
/*
 * Mutation to create prices for a rate
 */
export const useCreatePricesMutation = ({apartmentId, rateId}) => {
    const queryClient = useQueryClient();
    const {showSuccessNotification} = useFeedbackTools();

    return useMutation({
        mutationFn: (variables) => api.RatesAPI.postPrices(rateId, variables),
        onSuccess: () => {
            // Invalidate all the queries that are affected by the update
            // do not specify the date because the mobile version use different
            // dates than the desktop versions
            queryClient.invalidateQueries(['apartmentPrices', apartmentId])
            showSuccessNotification("Prezzi configurati correttamente");
        },
    })
}

/*
 * Mutation to update prices for a rate
 */
export const useUpdatePricesMutation = ({apartmentId}) => {
    const queryClient = useQueryClient();
    const {showSuccessNotification} = useFeedbackTools();

    return useMutation({
        mutationFn: (variables) => api.RatesAPI.patchPrices(variables.rateId, variables.data),
        onSuccess: (data, variables) => {
            // Invalidate all the queries that are affected by the update
            const months = eachMonthOfInterval({
                start: parseISO(variables.data.dateFrom),
                end: parseISO(variables.data.dateTo)
            })
            months.forEach(async (month) => {
                const fromISO = formatISO(startOfMonth(month), {representation: 'date'})
                const toISO = formatISO(endOfMonth(month), {representation: 'date'})
                await queryClient.invalidateQueries(['apartmentPrices', apartmentId, fromISO, toISO])
            });
            showSuccessNotification("Prezzi aggiornati correttamente");
        },
    })
}